import { toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { CONTACT_API } from 'modules/contacts/api/contact-api'
import {
  ImportContactsErrorResponseContentInterface,
  SendImportContactsJsonData,
} from 'modules/contacts/contacts-import/hook/use-send-import-contacts/types'
import { transformSendImportContactsJsonDataToFormData } from 'modules/contacts/contacts-import/hook/use-send-import-contacts/utils'
import { ContactsImportTableColumnId } from 'modules/contacts/contacts-import/types/contacts-import-column-select'
import {
  ContactsImportStatus,
  ContactsLastImportsInterface,
} from 'modules/contacts/contacts-import/types/contacts-import-interface'
import React, { useState } from 'react'
import TransLoco from 'shared/components/trans-loco'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { HTMLString } from 'shared/types/html-string'
import { getContactsPageNextjsUrl } from '../../../utils/get-contacts-page-nextjs-url'

export function useSendImportContacts() {
  const { t } = useLocoTranslation()

  const [errors, setErrors] = useState<{
    common?: HTMLString | null
    columns: Record<ContactsImportTableColumnId, HTMLString | null>
  } | null>(null)

  const { fetcher, isFetching } = useFetch<
    RequestMethodsEnum.postForm,
    ContactsLastImportsInterface
  >({
    method: RequestMethodsEnum.postForm,
  })

  return {
    fetcherSendImportContacts: async (data: SendImportContactsJsonData) => {
      try {
        setErrors(null)
        const formData = await transformSendImportContactsJsonDataToFormData(data)
        const newImportContactsData = await fetcher(`${CONTACT_API}/contacts/import`, formData)
        switch (newImportContactsData.status) {
          case ContactsImportStatus.suspended: {
            toast.success(t('dashboard.contact_import.suspended_message'))
            break
          }
          case ContactsImportStatus.approved: {
            toast.success(
              <TransLoco
                t={t}
                i18nKey={'dashboard.contact_import.form.toast.importing_your_contacts'}
                components={{
                  a: (
                    <a
                      href={`/dashboard${getContactsPageNextjsUrl()}`}
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />,
            )
            break
          }
          case ContactsImportStatus.failed: {
            toast.error(t('global.error'))
            break
          }
        }
      } catch (e) {
        if (e instanceof BadRequest) {
          const errors = e.errors as ImportContactsErrorResponseContentInterface
          const { mapColumns, ...rest } = errors.fields
          setErrors(prevError => ({
            ...prevError,
            common: errors.common.join('\n'),
            columns: Object.entries(mapColumns?.columns || {})?.reduce(
              (resColumns, [key, value]) => ({ ...resColumns, [key]: value?.join('') }),
              {},
            ),
          }))
          Object.entries(rest).forEach(el => {
            toast.error(el.join('\n'))
          })
        }
        throw e
      }
    },
    isSendImportContactsFetching: isFetching,
    sendImportContactsError: errors,
    resetSendImportContactsError: () => setErrors(null),
  }
}
